import {Inject, Injectable} from '@angular/core';
import {CplItem} from '../model/cpl-item.model';
import {UserAssignmentRequest} from '../model/user-assignment-request.model';
import {CplCycle} from '../model/cpl-cycle.model';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { CplItemDuplicateData } from '../model/cpl-item-duplicate-data.model';
import { BaseApi, BASE_URL } from '@edl-development/edl-common';
import { CplArchiveCycle } from '../model/cpl-archive-cycle.model';

@Injectable()
export class ComplianceItemApi extends BaseApi {
    constructor(
        @Inject(BASE_URL) private baseUri: string,
        private http: HttpClient
    ) {
        super();
    }

    getComplianceItem(cplId: string, cycleId: string) {
        return this.http.get<CplItem>(this.baseUri + '/cplitem/' + cplId + '?cycleID=' + cycleId);
    }

    getMyComplianceItems(offset: number, limit: number, orderBy: string, ascending: boolean, searchBy: string, status: number, filterByTag:string) {
        const params = new HttpParams()
            .append('offset', String(offset))
            .append('limit', String(limit))
            .append('orderBy', this.convertParam(orderBy))
            .append('ascending', String(ascending))
            .append('searchBy', this.convertParam(searchBy))
            .append('status', String(status))
            .append('tag', this.convertParam(filterByTag));

        return this.http.get(this.baseUri + '/cplitems/myitems', {
            params
        })
    }

    getComplianceItemsAssignedToUser(userId: string) {
        return this.http.get(this.baseUri + '/cplitems/' + userId);
    }

    getComplianceItems(offset: number, limit: number, orderBy: string, ascending: boolean, searchBy: string, status: number, tag: number) {
        const params = new HttpParams()
            .append('offset', String(offset))
            .append('limit', String(limit))
            .append('orderBy', this.convertParam(orderBy))
            .append('ascending', String(ascending))
            .append('searchBy', this.convertParam(searchBy))
            .append('status', String(status))
            .append('tag', String(tag));

        return this.http.get(this.baseUri + '/cplitems', {
            params
        });
    }

    createComplianceItem(complianceItem: CplItem) {
        return this.http.post(this.baseUri + '/cplitem', complianceItem);
    }

    updateComplianceItem(complianceItem: CplItem) {
        return this.http.put(this.baseUri + '/cplitem', complianceItem);
    }

    deleteComplianceItem(complianceItem: CplItem) {
        const httpOptions = {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' }), body: complianceItem
        };

        return this.http.request('delete', this.baseUri + '/cplitem', {
            responseType: 'text',
            body: complianceItem
        });
    }

    restoreComplianceItem(complianceItem: CplItem) {
        return this.http.put(this.baseUri + '/cplitem/restore', complianceItem, { responseType: 'text' });
    }

    removeComplianceItem(complianceItem: CplItem) {
        return this.http.request('delete', this.baseUri + '/cplitem/remove', {
            responseType: 'text',
            body: complianceItem
        });
    }

    archiveComplianceItem(complianceItem: CplItem) {
        return this.http.put(this.baseUri + '/cplitem/archive', complianceItem, { responseType: 'text' });
    }

    getArchiveWarnings(cplId: string) {
        return this.http.get(this.baseUri + '/cplitem/archiveWarnings/' + cplId);
    }

    activateComplianceItem(complianceItem: CplItem) {
        return this.http.put(this.baseUri + '/cplitem/activate', complianceItem, { responseType: 'text' });
    }

    convertComplianceItemToTemplate(complianceItemDuplicateData: CplItemDuplicateData) {
        return this.http.put(this.baseUri + '/cplitem/template', complianceItemDuplicateData);
    }

    duplicateComplianceItem(complianceItemDuplicateData: CplItemDuplicateData) {
        return this.http.put(this.baseUri + '/cplitem/duplicate', complianceItemDuplicateData);
    }

    getItemUserAssignments({
        cplId, 
        cycleId, 
        offset, 
        limit, 
        orderBy, 
        ascending,
        searchBy, 
        statusId, 
        a48statusId,
        approverUserId,
        groupIds
    }: {
        cplId: string, 
        cycleId: string,
        offset: number, 
        limit: number, 
        orderBy?: string, 
        ascending?: boolean,
        searchBy?:string, 
        statusId?:number, 
        a48statusId?:number,
        approverUserId?:string,
        groupIds?:string[]
    }) {
        const params = new HttpParams()
            .append('offset', String(offset))
            .append('limit', String(limit))
            .append('orderBy', String(orderBy))
            .append('ascending', String(ascending))
            .append('searchBy', this.convertParam(searchBy))
            .append('status', String(statusId))
            .append('a48status', a48statusId? String(a48statusId): "")
            .append('approverUserId', approverUserId ? String(approverUserId) : '')
            .append('groupIds', this.convertArrayParam(groupIds));

        return this.http.get(this.baseUri + '/UserAssignments/' + cplId + '/' + cycleId, {
            params
        });
    }

    getUserUserAssignments({ 
        cplId, 
        offset, 
        limit,
        orderBy, 
        ascending,
        searchBy, 
        statusId, 
        a48statusId,
        approverUserId,
        groupIds
    } : {
        cplId: string,
        offset: number, 
        limit: number, 
        orderBy?: string, 
        ascending?: boolean,
        searchBy?:string, 
        statusId?:number, 
        a48statusId?:number,
        approverUserId?:string,
        groupIds?:string[]
    }) {
        const params = new HttpParams()
            .append('offset', String(offset))
            .append('limit', String(limit))
            .append('orderBy', String(orderBy))
            .append('ascending', String(ascending))
            .append('searchBy', this.convertParam(searchBy))
            .append('status', String(statusId))
            .append('a48status', a48statusId? String(a48statusId): "")
            .append('approverUserId', approverUserId ? String(approverUserId) : '')
            .append('groupIds', this.convertArrayParam(groupIds));

        return this.http.get(this.baseUri + '/UserAssignments/' + cplId, {
            params
        });
    }

    removeUserAssignments(request: UserAssignmentRequest) {
        return this.http.request('delete', this.baseUri + '/UserAssignment', {
            responseType: 'text',
            body: request
        });
    }

    createUserAssignments(request: UserAssignmentRequest) {
        return this.http.post(this.baseUri + '/UserAssignment', request, { responseType: 'text' });
    }

    createBulkUserAssignments(fileList: FileList, ItemID: string){
        let file: File = fileList[0];
        let formData: FormData = new FormData();
        formData.append('file', file, file.name);
        formData.append('ItemID', ItemID);

        return this.http.post(this.baseUri + '/UserAssignment/bulk', formData);
    }

    createBulkSingleUserToMultipleAssignments(requests: UserAssignmentRequest[]) {
        return this.http.post(this.baseUri + '/UserAssignment/bulk/SingleUserToMultiple', requests);
    }

    updateUserAssignments(userCycles: CplCycle[]) {
        return this.http.put(this.baseUri + '/UserAssignments', userCycles, { responseType: 'text' });
    }

    getUserSummary(userId: string, offset: number, limit: number, orderBy: string, ascending: boolean, searchBy: string) {
        const params = new HttpParams()
            .append('offset', String(offset))
            .append('limit', String(limit))
            .append('orderBy', this.convertParam(orderBy))
            .append('ascending', String(ascending))
            .append('searchBy', this.convertParam(searchBy));

        return this.http.get(this.baseUri + '/UserSummary/' + userId, {
            params
        });
    }
    getUserArchiveSummary(userId: string) {

        return this.http.get<CplArchiveCycle[]>(this.baseUri + '/UserSummary/ArchiveSuper/' + userId);
    }

    getUserCycleHistoryByCycle(cycleId: string, offset: number, limit: number, orderBy: string, ascending: boolean, searchBy?: string, baseUrl?: string) {
        const params = new HttpParams()
            .append('offset', String(offset))
            .append('limit', String(limit))
            .append('orderBy', this.convertParam(orderBy))
            .append('ascending', String(ascending))
            .append('searchBy', this.convertParam(searchBy))
            .append('baseUrl', this.convertParam(baseUrl));

        return this.http.get(this.baseUri + '/UserAssignment/Cycle/History/' + cycleId , {
            params
        });
    }

    getExpiredCycles(cplId: string, offset: number, limit: number, orderBy: string, ascending: boolean, searchBy?: string, baseUrl?: string) {
        const params = new HttpParams()
            .append('offset', String(offset))
            .append('limit', String(limit))
            .append('orderBy', this.convertParam(orderBy))
            .append('ascending', String(ascending))
            .append('searchBy', this.convertParam(searchBy))
            .append('baseUrl', this.convertParam(baseUrl));

        return this.http.get(this.baseUri + '/UserAssignment/expired/' + cplId, {
            params
        });
    }
    getUserCycleHistory(cplId: string, userId: string, offset: number, limit: number, orderBy: string, ascending: boolean, searchBy?: string, baseUrl?: string) {
        const params = new HttpParams()
            .append('offset', String(offset))
            .append('limit', String(limit))
            .append('orderBy', this.convertParam(orderBy))
            .append('ascending', String(ascending))
            .append('searchBy', this.convertParam(searchBy))
            .append('baseUrl', this.convertParam(baseUrl));

        return this.http.get(this.baseUri + '/UserAssignmentHistory/' + cplId + '/' + userId, {
            params
        });
    }

    getUserCompliance(offset: number, limit: number, orderBy: string, ascending: boolean, searchBy?: string, baseUrl?: string) {
        const params = new HttpParams()
            .append('offset', String(offset))
            .append('limit', String(limit))
            .append('orderBy', this.convertParam(orderBy))
            .append('ascending', String(ascending))
            .append('searchBy', this.convertParam(searchBy))
            .append('baseUrl', this.convertParam(baseUrl));

        return this.http.get(this.baseUri + '/usercompliance', {
            params
        });
    }

    getTags(searchBy: string) {
        return this.http.get(this.baseUri + '/cplitem/tag/' + searchBy);
    }



    getPrintableRoster(complianceItemId: number) {
        return this.http.get(this.baseUri + '/UserAssignments/roster/' + complianceItemId.toString());
    }
}